import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Pin, PinOff, Check, Settings2, Loader2, AlertTriangle } from 'lucide-react'
import { popupFingerprint, noteAuthor, noteEdited, noteId, canUnpinNote, mergeNote } from '../../lib/patientNotes.js'
import { fmtDate, fmtTime } from '../../lib/datetime.js'
import { nameInitial } from '../../lib/text.js'
import { postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'

/**
 * The full-screen โน้ตสำคัญ interrupt — `patient.note[].popup === true`.
 *
 * WHY AN INTERRUPT AT ALL, when the pin chip in the header already shows these.
 * The chip is a one-line preview in a row that also carries allergy, disease and
 * bleeding-risk chips. Someone opening forty patients a day stops parsing that
 * row inside a week — and a popup note's job is precisely to stop you before you
 * do the usual thing (ค้างเงิน, ห้ามให้เครดิต, เคยร้องเรียน). A chip cannot do
 * that job. The chip is not replaced; it carries the note for the rest of the
 * session once this has been acknowledged.
 *
 * WHY IT DOES NOT FIRE ON EVERY OPEN. A modal on every page load trains
 * dismissal: within a week staff press รับทราบ before reading, and then it costs
 * a click AND conveys nothing — strictly worse than the chip. So it fires only
 * on FIRST CONTACT and on CHANGE:
 *
 *   · once per browser SESSION per patient, and
 *   · again whenever the popup notes themselves change — popupFingerprint()
 *     folds in `modified_date`, so an edited note re-interrupts even in the same
 *     session. Without that, "ค้างเงิน 5,000" edited to "ค้างเงิน 20,000" would
 *     never be seen by anyone who had already acknowledged the old text.
 *
 * WHY sessionStorage AND NOT localStorage. A new shift opening the browser is a
 * new session, so the 17:00 person gets the same warning the 09:00 person got.
 * localStorage would silence it permanently after one acknowledgement, which is
 * exactly wrong for something a colleague deliberately flagged. It is also
 * per-tab, which matches "one person at one station".
 *
 * ⚠️ MOUNT IT WITH key={patientId}. The acknowledgement is read synchronously in
 * a lazy initialiser, so a remount per patient is what stops the previous
 * patient's answer being applied to this one for a frame.
 *
 * NOT DISMISSIBLE by backdrop click or Escape — the only modal in the app that
 * is not. There is one way out and it is the button, because "I clicked
 * somewhere and it vanished" is indistinguishable from "I read it".
 *
 * เลิกเตือน UNPINS THE NOTE FROM HERE, WHOEVER WROTE IT. An alert nobody can
 * retire becomes noise, and the person reading a stale one is the person best
 * placed to retire it — making them navigate to ติดตาม to do it is how stale
 * popups survive for years. Restricting it to the author was worse still: the
 * colleague who pinned "ค้างเงิน 5,000" in 2023 may not work here any more, and
 * everyone else was left acknowledging a dead alarm forever.
 *
 * So this posts `action: 'unpin'`, which functions/api/patients/note.js allows
 * across authors — and which can ONLY flip `popup`, because the proxy re-sends
 * the STORED details rather than anything sent from here. The note's text and
 * byline are untouched; editing and deleting are still your own notes only.
 * Unpinning the last one unmounts this component — there is nothing left to warn
 * about.
 */
export default function PatientNoteAlert({ patientId, patientName, notes, me, setPatient }) {
  const fp = useMemo(() => popupFingerprint(notes), [notes])
  const [ack, setAck] = useState(() => readAck(patientId))
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  const open = !!fp && ack !== fp

  // The scroll lock is the only body side-effect; without it a long note list
  // scrolls the page behind the overlay instead of itself.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  if (!open) return null

  function acknowledge() {
    writeAck(patientId, fp)
    setAck(fp)
  }

  // The local merge runs only AFTER the server agrees, so a stale uid never
  // leaves the modal showing an unpin that did not happen. mergeNote keeps the
  // enriched fields — assigning the write's response would drop note[].code and
  // blank every byline in the header.
  //
  // No `details` is sent: `unpin` reads the stored text server-side, which is
  // exactly what lets it work on a colleague's note without being able to change
  // a word of it.
  async function unpin(n) {
    const uid = noteId(n)
    if (!uid || busy) return
    setBusy(uid); setErr('')
    try {
      await postJson('/api/patients/note', { patient_id: patientId, action: 'unpin', unique_id: uid })
      setPatient?.((p) => (p ? { ...p, note: mergeNote(p.note, 'unpin', { unique_id: uid, me }) } : p))
    } catch (e) {
      setErr(apiErrorText(e, 'เลิกเตือนไม่สำเร็จ'))
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" role="alertdialog" aria-modal="true"
      aria-label="โน้ตสำคัญของคนไข้">
      <div className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]" />
      <div className="relative w-full max-w-[520px] bg-white rounded-2xl shadow-2xl shadow-slate-900/25 ring-1 ring-amber-200 overflow-hidden animate-ss-pop-in">
        <div className="flex items-start gap-3 px-5 py-4 bg-amber-50 border-b border-amber-200">
          <span className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200 flex items-center justify-center flex-none">
            <Pin size={17} strokeWidth={2.4} />
          </span>
          <div className="min-w-0">
            <div className="text-[15.5px] font-semibold text-amber-900">
              โน้ตสำคัญ{notes.length > 1 ? ` ${notes.length} รายการ` : ''}
            </div>
            {patientName && <div className="text-[12.5px] text-amber-700/80 mt-0.5 truncate">{patientName}</div>}
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto divide-y divide-slate-100">
          {notes.map((n, i) => (
            <NoteBlock key={n.unique_id || i} n={n} me={me}
              busy={busy === noteId(n)} onUnpin={() => unpin(n)} />
          ))}
        </div>

        {err && (
          <div className="px-5 py-2.5 bg-rose-50 border-t border-rose-200 text-[12.5px] text-rose-700 flex items-start gap-1.5">
            <AlertTriangle size={13} className="flex-none mt-0.5" />{err}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-slate-100 bg-slate-50/60">
          {/* An alert nobody can retire becomes noise. Whoever reads a stale note
              is the person best placed to unpin it, so the way to do that is
              one click from here. */}
          <Link to={`/patient/${patientId}/follow-ups`} onClick={acknowledge}
            className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-brand-700">
            <Settings2 size={13} /> จัดการโน้ต
          </Link>
          <button type="button" onClick={acknowledge} autoFocus
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[14px] font-semibold text-white bg-amber-600 hover:bg-amber-700">
            <Check size={15} strokeWidth={2.5} /> รับทราบ
          </button>
        </div>
      </div>
    </div>
  )
}

// The byline is not decoration. A note from 2023 carrying the same weight as one
// written this morning is how staff learn to distrust all of them — so an old
// alarm has to LOOK old, in words, not in a date they have to subtract from
// today.
function NoteBlock({ n, me, busy, onUnpin }) {
  const when = Number(n?.timestamp) || 0
  // Mirrors functions/api/patients/note.js: unpin is open to every author's
  // note, but a note with no unique_id (a local placeholder awaiting the
  // server's uuid) cannot be addressed at all. Rendered DISABLED rather than
  // hidden — a missing button reads as a missing feature, and the title says so.
  const addressable = !!noteId(n)
  const canUnpin = addressable && canUnpinNote(n, me)
  return (
    <div className="px-5 py-4">
      {/* pre-wrap is the readability of this field, not styling: popup notes are
          routinely multi-line running deposit ledgers kept over years. */}
      <p className="text-[14px] text-slate-800 leading-relaxed whitespace-pre-wrap break-words">{n.details}</p>
      <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-slate-400 flex-wrap">
        <span className="w-4 h-4 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[9px] font-semibold shrink-0">
          {n.code ? nameInitial(n.code) : '·'}
        </span>
        <span className="font-medium text-slate-500">{noteAuthor(n)}</span>
        {when > 0 && <span className="tabular-nums">· {fmtDate(when)} {fmtTime(when)}</span>}
        {ageLabel(when) && <span>· {ageLabel(when)}</span>}
        {noteEdited(n) && <span>· แก้ไข {fmtDate(n.modified_date)}</span>}
        <button type="button" onClick={onUnpin} disabled={!canUnpin || busy}
          title={canUnpin ? 'เลิกปักหมุด — จะไม่เตือนเต็มจออีก (ข้อความโน้ตยังอยู่)'
            : 'โน้ตนี้ไม่มีรหัสอ้างอิง จึงเลิกปักหมุดไม่ได้'}
          className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11.5px] font-medium text-slate-500 hover:text-amber-700 hover:bg-amber-50 disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <PinOff size={12} />} เลิกเตือน
        </button>
      </div>
    </div>
  )
}

function ageLabel(ts) {
  if (!ts) return ''
  const days = Math.floor((Date.now() - ts) / 86400000)
  if (days < 0) return ''
  if (days === 0) return 'วันนี้'
  if (days < 31) return `${days} วันก่อน`
  const months = Math.floor(days / 30.44)
  if (months < 12) return `${months} เดือนก่อน`
  const years = Math.floor(days / 365.25)
  return `${years} ปีก่อน`
}

// Storage is best-effort: Safari private mode throws on write. A failure must
// mean "alert again", never "crash the patient page" — so both sides swallow.
const ackKey = (pid) => `ssdc.noteack.${pid}`
function readAck(pid) {
  try { return sessionStorage.getItem(ackKey(pid)) } catch { return null }
}
function writeAck(pid, fp) {
  try { sessionStorage.setItem(ackKey(pid), fp) } catch { /* private mode */ }
}
